/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import { canPanelManagePremium } from '../../panelAccess/panelRoleAccess';
import { useModerationManageState, type TabKey } from './hooks/useModerationManageState';
import { useModerationManageActions } from './hooks/useModerationManageActions';
import PlayerProfileSection from './components/moderation/PlayerProfileSection';
import BuildingsModerationTab from './components/moderation/BuildingsModerationTab';
import VehiclesModerationTab from './components/moderation/VehiclesModerationTab';
import StaffModerationTab from './components/moderation/StaffModerationTab';
import InterventionsModerationTab from './components/moderation/InterventionsModerationTab';

const TAB_LABELS: Record<TabKey, string> = {
  buildings: 'Bâtiments',
  vehicles: 'Véhicules',
  staff: 'Personnel',
  interventions: 'Interventions',
};

/**
 * Page « Gestion possessions joueur » (panel V1).
 *
 * Orchestrateur fin : tout l'état + tous les handlers sont déplacés dans
 * deux hooks (`useModerationManageState`, `useModerationManageActions`),
 * et chaque section JSX est extraite dans un sous-composant
 * (PlayerProfileSection + 4 *ModerationTab).
 */
const PanelModerationManageV1: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ userId: string }>();
  const userId = Number(params.userId || 0);
  const { currentUser } = useSelector((state: RootState) => state.user);
  const canManagePremium = canPanelManagePremium(currentUser?.roles_id ?? 99);

  const state = useModerationManageState({ userId });
  const actions = useModerationManageActions({
    userId,
    canManagePremium,
    tab: state.tab,
    editForm: state.editForm,
    setEditForm: state.setEditForm,
    editingId: state.editingId,
    setEditingId: state.setEditingId,
    setError: state.setError,
    vehicleStatusCodeById: state.vehicleStatusCodeById,
    vehicleStatusCodeByName: state.vehicleStatusCodeByName,
    playerProfile: state.playerProfile,
    setSavingProfile: state.setSavingProfile,
    economyAction: state.economyAction,
    setEconomyAction: state.setEconomyAction,
    setEconomyAdjusting: state.setEconomyAdjusting,
    generatedScenarioId: state.generatedScenarioId,
    load: state.load,
    loadProfile: state.loadProfile,
    loadCtaBuildings: state.loadCtaBuildings,
  });

  const thCls =
    'border-b border-[#2b3a61] bg-[#0a1329] px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400';
  const tdCls = 'border-b border-[#263455] px-2 py-2 align-middle text-[11px] text-slate-200';

  return (
    <div className="rounded-lg border border-[#263455] bg-[#0b1734] p-3">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Gestion possessions — joueur #{userId}</h2>
        <button
          type="button"
          onClick={() => navigate('/panel/moderation')}
          className="rounded bg-slate-700 px-3 py-1 text-xs text-white hover:bg-slate-600"
        >
          Retour
        </button>
      </div>

      {state.playerProfile && (
        <PlayerProfileSection
          playerProfile={state.playerProfile}
          setPlayerProfile={state.setPlayerProfile}
          canManagePremium={canManagePremium}
          economyAction={state.economyAction}
          setEconomyAction={state.setEconomyAction}
          economyAdjusting={state.economyAdjusting}
          savingProfile={state.savingProfile}
          adjustEconomy={actions.adjustEconomy}
          savePlayerProfile={actions.savePlayerProfile}
        />
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        {(['buildings', 'vehicles', 'staff', 'interventions'] as TabKey[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => {
              state.setTab(k);
              state.setEditingId(null);
              state.setEditForm({});
            }}
            className={`rounded px-3 py-1 text-xs ${state.tab === k ? 'bg-[#4b57ff] text-white' : 'bg-[#101f44] text-slate-200'}`}
          >
            {TAB_LABELS[k]}
          </button>
        ))}
      </div>

      <div className="mb-3 flex items-center gap-2 rounded border border-[#33456f] bg-[#101f44] px-2 py-2">
        <span className="text-xs text-slate-300">Recherche ({TAB_LABELS[state.tab]})</span>
        <input
          value={state.searchByTab[state.tab]}
          onChange={(e) => state.setSearchByTab((prev) => ({ ...prev, [state.tab]: e.target.value }))}
          placeholder="Nom, ID, statut, adresse..."
          className="w-full min-w-[220px] rounded border border-[#33456f] bg-[#0a1329] px-2 py-1 text-xs text-slate-100 placeholder:text-slate-500"
        />
        <span className="text-[11px] text-slate-500">
          {state.filteredItems.length}/{state.items.length}
        </span>
      </div>

      {state.tab === 'interventions' && (
        <div className="mb-3 rounded border border-[#33456f] bg-[#101f44] p-3 text-xs text-slate-200">
          <div className="mb-2 font-semibold text-white">
            Générer une intervention (scénario CTA — adresse aléatoire, comme &quot;tester sur mon
            compte&quot;)
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-[11px] text-slate-400">
              <span>Scénario</span>
              <select
                value={state.generatedScenarioId}
                onChange={(e) => state.setGeneratedScenarioId(e.target.value)}
                className="min-w-[280px] rounded border border-[#33456f] bg-[#0a1329] px-2 py-1.5 text-xs text-slate-100"
              >
                <option value="">Sélectionner un scénario CTA</option>
                {state.ctaScenarios.map((s) => (
                  <option key={s.id} value={String(s.id)}>
                    #{s.id} — {s.scenario_name || 'Scénario'}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={() => void actions.generateFromScenario()}
              className="rounded bg-indigo-600 px-3 py-1.5 text-xs text-white hover:bg-indigo-500"
            >
              Générer
            </button>
          </div>
        </div>
      )}

      {state.loading && <p className="text-slate-300">Chargement...</p>}
      {state.error && <p className="mb-2 text-red-300">{state.error}</p>}

      {!state.loading && state.tab === 'buildings' && (
        <BuildingsModerationTab
          userId={userId}
          filteredItems={state.filteredItems}
          thCls={thCls}
          tdCls={tdCls}
          editingId={state.editingId}
          editForm={state.editForm}
          setEditForm={state.setEditForm}
          startEdit={actions.startEdit}
          cancelEdit={actions.cancelEdit}
          handleSave={actions.handleSave}
          handleDelete={actions.handleDelete}
          toggleBuildingActive={actions.toggleBuildingActive}
          updateBuildingCta={actions.updateBuildingCta}
          buildingCtaLink={state.buildingCtaLink}
          setBuildingCtaLink={state.setBuildingCtaLink}
          ctaBuildings={state.ctaBuildings}
          possessionData={state.possessionData}
        />
      )}

      {!state.loading && state.tab === 'vehicles' && (
        <VehiclesModerationTab
          userId={userId}
          filteredItems={state.filteredItems}
          thCls={thCls}
          tdCls={tdCls}
          editingId={state.editingId}
          editForm={state.editForm}
          setEditForm={state.setEditForm}
          startEdit={actions.startEdit}
          cancelEdit={actions.cancelEdit}
          handleSave={actions.handleSave}
          handleDelete={actions.handleDelete}
          lookup={state.lookup}
          buildingOptions={state.buildingOptions}
        />
      )}

      {!state.loading && state.tab === 'staff' && (
        <StaffModerationTab
          userId={userId}
          filteredItems={state.filteredItems}
          thCls={thCls}
          tdCls={tdCls}
          editingId={state.editingId}
          editForm={state.editForm}
          setEditForm={state.setEditForm}
          startEdit={actions.startEdit}
          cancelEdit={actions.cancelEdit}
          handleSave={actions.handleSave}
          handleDelete={actions.handleDelete}
          lookup={state.lookup}
          buildingOptions={state.buildingOptions}
        />
      )}

      {!state.loading && state.tab === 'interventions' && (
        <InterventionsModerationTab
          userId={userId}
          filteredItems={state.filteredItems}
          thCls={thCls}
          tdCls={tdCls}
          editingId={state.editingId}
          editForm={state.editForm}
          setEditForm={state.setEditForm}
          startEdit={actions.startEdit}
          cancelEdit={actions.cancelEdit}
          handleSave={actions.handleSave}
          handleDelete={actions.handleDelete}
          resetIntervention={actions.resetIntervention}
          cancelIntervention={actions.cancelIntervention}
        />
      )}
    </div>
  );
};

export default PanelModerationManageV1;
